import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';

import { Column } from '@simplywallst/ui-core';

import { useContentfulImage } from '@/hooks/useContentfulImage';
import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';

import {
  BenefitsContainerRow,
  BenefitsHeader,
  BenefitsContentRow,
  StyledReactMarkdown,
  VideoContainer,
} from './styled';
import { OneColumnExpanded } from '../../styled';

interface Image {
  title?: string;
  description?: string;
  url?: string;
}

interface Props {
  title?: string;
  subtitle?: string;
  markdownContent: string[];
  gallery?: Image[];
}

const BenefitImage = ({ image }: { image: Image }) => {
  const imageContentful = useContentfulImage({
    url: image && image.url,
    title: image ? image.title : '',
    format: 'avif',
  });
  const imageContentfulFallback = useContentfulImage({
    url: image && image.url,
    title: image ? image.title : '',
  });

  return (
    <>
      <Helmet>
        <link rel="prefetch" href={imageContentful?.src} />
        <link rel="prefetch" href={imageContentfulFallback?.src} />
      </Helmet>
      {imageContentful && imageContentfulFallback && (
        <DeferRenderUntilIntersecting>
          {() => (
            <picture>
              <source srcSet={imageContentful.srcSet} type="image/avif" />
              <img
                src={imageContentfulFallback.src}
                alt={image.title ?? ''}
                height="600"
              />
            </picture>
          )}
        </DeferRenderUntilIntersecting>
      )}
    </>
  );
};

export const Benefits = ({
  title,
  subtitle,
  gallery,
  markdownContent,
}: Props) => {
  return (
    <BenefitsContainerRow justifyContent="center">
      <OneColumnExpanded>
        <BenefitsHeader data-cy-id="benefits">
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </BenefitsHeader>
        <BenefitsContentRow>
          <Column
            colMd={6}
            displayFlex
            alignItem="center"
            alignItemSm="flex-start"
          >
            {gallery?.[0] && <BenefitImage image={gallery[0]} />}
          </Column>
          <Column colMd={6} displayFlex alignItem="center">
            <StyledReactMarkdown>
              <ReactMarkdown source={markdownContent[0]} />
            </StyledReactMarkdown>
          </Column>
        </BenefitsContentRow>
        <BenefitsContentRow rowReverseSm>
          <Column
            colMd={6}
            displayFlex
            alignItem="center"
            alignItemSm="flex-start"
          >
            {gallery?.[1] && <BenefitImage image={gallery[1]} />}
          </Column>
          <Column colMd={6} displayFlex alignItem="center">
            <StyledReactMarkdown>
              <ReactMarkdown source={markdownContent[1]} />
            </StyledReactMarkdown>
          </Column>
        </BenefitsContentRow>
        <BenefitsContentRow>
          <Column
            colMd={6}
            displayFlex
            alignItem="center"
            alignItemSm="flex-start"
          >
            {gallery?.[2] && <BenefitImage image={gallery[2]} />}
          </Column>
          <Column colMd={6} displayFlex alignItem="center">
            <StyledReactMarkdown>
              <ReactMarkdown source={markdownContent[2]} />
            </StyledReactMarkdown>
          </Column>
        </BenefitsContentRow>
        <VideoContainer>
          <iframe
            width="560"
            height="315"
            src={`https://www.youtube.com/embed/YVBz7yZixvA`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </VideoContainer>
      </OneColumnExpanded>
    </BenefitsContainerRow>
  );
};
